import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { AUTOMATION_CREATION_TYPE } from 'constants/feature/element-panel-constants';
import AutomaticallyIdentifiedAnnotations from 'components/common/automatically-identified-annotations';
import { shouldRestrictAutomaticIdentifier } from 'utils/statement-content-page-utils';
import { ReactComponent as FlaggedIcon } from 'icons/flag-icon.svg';
import { getFlaggedWorkpapers } from 'constants/feature/tieout-element-constants';
import classNames from 'classnames';

export const WORKPAPER_ENTRY_BLOCK = 'workpaper-entry';
export const WORKPAPER_ENTRY_ID_BLOCK = 'workpaper-entry-id';
const WORKPAER_ENTRY_FLAGGED_ICON_SIZE = '16px';

const WorkpaperEntry = ({ workpaper, rightComponent }) => {
  const {
    revision,
    elementPanel: { elementDetails },
  } = useSelector((state) => state.data);

  // Show the automatic annotation 'A' when:
  // 1. The revision allows automatic identifiers
  // 2. The workpaper was created through automation
  // 3. The overrideCreationType flag is not set (prevents showing 'A' when manually added to other elements)
  const shouldShowAutomaticAnnotation =
    !shouldRestrictAutomaticIdentifier(revision) &&
    Object.values(AUTOMATION_CREATION_TYPE).includes(workpaper.creationType) &&
    !workpaper?.overrideCreationType;

  const { flaggedWorkpapers } = getFlaggedWorkpapers(elementDetails);
  const isWorkpaperFlagged =
    flaggedWorkpapers.has(workpaper.referenceNumber) &&
    elementDetails.isWorkpaperFlagged?.();

  return (
    <div key={workpaper.referenceNumber} className={`${WORKPAPER_ENTRY_BLOCK}`}>
      <div className={`${WORKPAPER_ENTRY_BLOCK}__info`}>
        {shouldShowAutomaticAnnotation && (
          <AutomaticallyIdentifiedAnnotations
            tooltipId={WORKPAPER_ENTRY_ID_BLOCK}
          />
        )}
        <div className={`${WORKPAPER_ENTRY_BLOCK}__ordinal`}>
          <div
            className={classNames(
              `${WORKPAPER_ENTRY_BLOCK}__result-container`,
              isWorkpaperFlagged &&
                `${WORKPAPER_ENTRY_BLOCK}__result-container--flagged`,
            )}
          >
            {isWorkpaperFlagged ? (
              <FlaggedIcon
                className={`${WORKPAPER_ENTRY_BLOCK}__prefix-icon`}
                width={WORKPAER_ENTRY_FLAGGED_ICON_SIZE}
                height={WORKPAER_ENTRY_FLAGGED_ICON_SIZE}
              />
            ) : null}
            <div
              className={classNames(
                `${WORKPAPER_ENTRY_BLOCK}__ref-details`,

                {
                  [`${WORKPAPER_ENTRY_BLOCK}__ref-details--column`]:
                    workpaper.referenceName,
                },
              )}
            >
              <span className={`${WORKPAPER_ENTRY_BLOCK}__ref-num`}>
                {workpaper.referenceNumber}
              </span>
              {workpaper.referenceName && (
                <span className={`${WORKPAPER_ENTRY_BLOCK}__ref-name`}>
                  {workpaper.referenceName}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {rightComponent}
    </div>
  );
};

WorkpaperEntry.propTypes = {
  /** Workpaper object from the workpaper list endpoint */
  workpaper: PropTypes.shape({
    workpaperRefId: PropTypes.number.isRequired,
    referenceName: PropTypes.string,
    referenceNumber: PropTypes.string.isRequired,
    workpaperType: PropTypes.string,
    creationType: PropTypes.string,
    overrideCreationType: PropTypes.bool,
  }),
  /** Component used to children to the right of the main content, displayed as flex container with content*/
  rightComponent: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
};

export default WorkpaperEntry;
